import { useMemo } from 'react';
import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard,
  Menu,
  DollarSign,
  CheckSquare,
  BarChart3,
  Users,
  Settings,
  Bell,
  Loader,
  Upload,
} from 'lucide-react';
import { useUIStore } from '@/shared/stores/uiStore';
import { useAuthorize } from '@/features/auth/hooks/useAuthorize';
import { PERMISSIONS } from '@/features/auth/constants/permissions';
import { ROLES } from '@/features/auth/constants/roles';
import type { AuthorizedNavItem } from '@/features/auth/types/authorization';

// ─── Navigation Items with Authorization ──────────────────────────
// Each item can specify required roles and/or permissions.
// Navigation items are automatically filtered based on user authorization.

const navItems: AuthorizedNavItem[] = [
  {
    path: '/dashboard',
    icon: LayoutDashboard,
    label: 'Dashboard',
    permissions: [PERMISSIONS.DASHBOARD_VIEW],
  },
  {
    path: '/expenses',
    icon: DollarSign,
    label: 'Expenses',
    permissions: [PERMISSIONS.EXPENSE_READ],
  },
  {
    path: '/expenses/processing',
    icon: Loader,
    label: 'Processing Queue',
    permissions: [PERMISSIONS.EXPENSE_READ],
  },
  {
    path: '/approvals',
    icon: CheckSquare,
    label: 'Approvals',
    permissions: [PERMISSIONS.APPROVAL_VIEW],
  },
  {
    path: '/expenses/erp-push',
    icon: Upload,
    label: 'Push to ERP',
    permissions: [PERMISSIONS.EXPENSE_UPDATE],
  },
  {
    path: '/reports',
    icon: BarChart3,
    label: 'Reports',
    permissions: [PERMISSIONS.REPORT_VIEW],
  },
  {
    path: '/users',
    icon: Users,
    label: 'Users',
    roles: [ROLES.ORG_ADMIN, ROLES.SUPER_ADMIN],
    permissions: [PERMISSIONS.USER_READ],
  },
  {
    path: '/notifications',
    icon: Bell,
    label: 'Notifications',
    permissions: [PERMISSIONS.NOTIFICATION_READ],
  },
  {
    path: '/settings',
    icon: Settings,
    label: 'Settings',
    roles: [ROLES.ORG_ADMIN, ROLES.FINANCE_MANAGER, ROLES.SUPER_ADMIN],
    permissions: [PERMISSIONS.SETTINGS_READ],
  },
];

export function Sidebar() {
  const { sidebarOpen, toggleSidebar } = useUIStore();
  const { isSuperAdmin, hasAnyPermission, hasAnyRole } = useAuthorize();

  // ─── Filter nav items based on authorization ─────────────────────

  const visibleNavItems = useMemo(() => {
    return navItems.filter((item) => {
      if (isSuperAdmin()) return true;

      const hasPermissions =
        !item.permissions ||
        item.permissions.length === 0 ||
        hasAnyPermission(...item.permissions);

      const hasRoles =
        !item.roles ||
        item.roles.length === 0 ||
        hasAnyRole(...item.roles);

      return hasPermissions && hasRoles;
    });
  }, [isSuperAdmin, hasAnyPermission, hasAnyRole]);

  return (
    <>
      {/* Mobile-only backdrop — tapping it closes the drawer, same as the toggle button. */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-20 bg-black/40 md:hidden"
          onClick={toggleSidebar}
          aria-hidden="true"
        />
      )}
      <aside
        className={`glass-nav fixed left-0 top-0 z-30 flex h-full w-64 flex-col border-r transition-transform md:transition-all ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } ${sidebarOpen ? 'md:w-64' : 'md:w-16 md:translate-x-0'}`}
      >
        <div className="flex h-14 items-center justify-between border-b px-4">
          {sidebarOpen ? (
            <span className="bg-gradient-to-br from-primary to-[color:var(--secondary-accent)] bg-clip-text text-lg font-bold text-transparent">EMS</span>
          ) : (
            <span className="bg-gradient-to-br from-primary to-[color:var(--secondary-accent)] bg-clip-text text-lg font-bold text-transparent">E</span>
          )}
          <button
            onClick={toggleSidebar}
            className="rounded-md p-1 text-muted-foreground hover:bg-accent"
            aria-label={sidebarOpen ? 'Close sidebar' : 'Open sidebar'}
          >
            <Menu className="h-4 w-4" />
          </button>
        </div>
        <nav className="flex-1 space-y-1 overflow-y-auto p-2">
          {visibleNavItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              onClick={() => {
                if (window.innerWidth < 768) toggleSidebar();
              }}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-xl px-3 py-2 text-sm transition-colors ${
                  isActive
                    ? 'bg-primary/10 font-medium text-primary shadow-[inset_0_0_0_1px_rgba(var(--primary-rgb),0.18)]'
                    : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                }`
              }
            >
              <item.icon className="h-4 w-4 shrink-0" />
              {sidebarOpen && <span>{item.label}</span>}
            </NavLink>
          ))}
        </nav>
      </aside>
    </>
  );
}
